import '@mdi/font/css/materialdesignicons.css';
import 'typeface-roboto';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';
import mixins from 'vue-typed-mixins';
import App from './App.vue';
import router from './router';
import Vuetify from 'vuetify/lib';
import axios from 'axios';
import i18n, { LanguageObject } from './i18n';
import '@/components/registered'; // import global registered custom components
import '@/components/framework'; // import global registered custom components
import Toast from '@/components/framework/toast';
import '@/validation';

import AuthUtils from './utils/AuthUtils';
import { APIError } from './utils/ErrorUtils';

Vue.config.productionTip = false;

axios.defaults.baseURL =
  process.env.VUE_APP_API_URL + process.env.VUE_APP_API_PREFIX;

axios.interceptors.response.use(
  async (response) => response,
  async (error) => {
    if (
      error.response &&
      error.response.status === 401 &&
      router.currentRoute.meta.auth !== false
    ) {
      AuthUtils.clear();
      router.push({ name: 'login' });
    }
    return Promise.reject(error);
  }
);

Vue.use(Vuetify);
const vuetify = new Vuetify({
  icons: {
    iconfont: 'mdi', // 'mdi' || 'mdiSvg' || 'md' || 'fa' || 'fa4' || 'faSvg'
  },
  lang: {
    t: (key, ...params) => i18n.t(key, params) as string, // will look in i18n files for $vuetify object for translations - https://vuetifyjs.com/en/customization/internationalization#create-translation
  },
  theme: {
    themes: {
      light: {
        primary: '#226CBF',
        secondary: '#424242',
        anchor: '#8c9eff',
        accent: '#82B1FF',
        error: '#FF5252',
        info: '#2196F3',
        success: '#4CAF50',
        warning: '#FFC107',
        exampleCustomColour: '9933ff', // this can then be used like: <ozi-button color="exampleCustomColour" class="exampleCustomColour"></ozi-button>

        oziGreen1: '#A7B366',
        oziGrey1: '#6D6E71',
        oziViolet1: '#C785FF',
        oziViolet2: '#9066B3',
      },
    },
  },
});

Vue.prototype.$toast = Toast;

Vue.prototype.$loading = function(state: boolean) {
  if (this.$data) {
    this.$root.$data.loading = state;
  }
};

Vue.prototype.$translate = function(message: LanguageObject) {
  if (message) {
    return i18n.locale
      ? message[i18n.locale]
      : message[i18n.fallbackLocale as string];
  }
};

Vue.prototype.$handleAPIError = function(error: APIError, observer?: any) {
  if (error && error.response) {
    const message = error.response.data.code
      ? i18n.t(`validation.error.${error.response.data.code}`).toString()
      : error.response.data.error;

    if (
      observer &&
      error.response.data.field &&
      Object.keys(observer.fields).includes(error.response.data.field)
    ) {
      observer.setErrors({ [error.response.data.field]: message });
    } else if (error.response.data.field) {
      return [`${error.response.data.field}: ${message}`];
    } else {
      return [message];
    }
  } else {
    console.error('unknown error', error);
    return [i18n.t(`validation.error.default`).toString()];
  }
  return [];
};

Vue.mixin({
  computed: {
    $uuid() {
      // give each component a uuid
      return uuidv4();
    },
    $locales(): string[] {
      return Object.keys(i18n.messages);
    },
  },

  methods: {
    async $confirm(message: string, callback?: Function) {
      return (this.$root as any)._oziConfirmFunction(message, callback);
    },
  },
});

new Vue({
  router,
  vuetify,
  i18n,
  data(): {
    loading: boolean;
  } {
    return {
      loading: true,
    };
  },
  render: (h) => h(App),
}).$mount('#app');

export { vuetify };
